import React, { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { useAppDispatch } from "../../common/hooks/default";
import { fetchRespyCalibrations } from "./respycalibrationManagement.thunk";
import { useSelector } from "react-redux";
import {
	selectRespyCalibrationError,
	selectRespyCalibrationIsLoading,
	selectRespyCalibrationSites,
} from "./respyCalbrationManagementSlice.selector";
import { If } from "../../common/components/If";
import {
	Alert,
	Box,
	LinearProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import ReactCountryFlag from "react-country-flag";
import { ActiveCalibrationField } from "./ActiveCalibrationField";

const styles = {
	wrapper: {
		display: "flex",
		flexDirection: "column",
		alignitems: "center",
		gap: "16px",
		margin: "32px",
	},
	container: {
		padding: "32px",
	},
	tableHead: {
		fontWeight: "bold",
		fontSize: "18px",
	},
	tableCell: {
		fontSize: "16px",
	},
};

export interface RespyCalibrationManagementPageProps {}

export function RespyCalibrationManagementPage(
	props: RespyCalibrationManagementPageProps
) {
	const dispatch = useAppDispatch();
	const isLoading = useSelector(selectRespyCalibrationIsLoading);
	const error = useSelector(selectRespyCalibrationError);
	const sites = useSelector(selectRespyCalibrationSites);

	const [search, setSearch] = useState("");

	const debouncedFetch = useMemo(
		() =>
			debounce((s: string) => {
				dispatch(fetchRespyCalibrations(s));
			}, 400),
		[dispatch]
	);

	useEffect(() => {
		debouncedFetch(search);
	}, [search]);

	return (
		<Box sx={styles.wrapper}>
			<Typography variant="h2" component="h2">
				Respy Calibrations
			</Typography>
			<If condition={error}>
				<Alert severity="error">{"" + JSON.stringify(error)}</Alert>
			</If>
			<If condition={isLoading}>
				<LinearProgress data-test-id="sites-calibrations-is-loading" />
			</If>
			<Box>
				<TextField
					disabled={isLoading}
					label="Search"
					variant="filled"
					value={search}
					onChange={(ev) => setSearch(ev.target.value)}
				/>
			</Box>
			<TableContainer component={Paper} sx={styles.container}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell sx={styles.tableHead}>Name</TableCell>
							<TableCell sx={styles.tableHead}>Id</TableCell>
							<TableCell sx={styles.tableHead}>Country</TableCell>
							<TableCell sx={styles.tableHead}>
								Installed capacity (MW)
							</TableCell>
							<TableCell sx={styles.tableHead}>
								Latitude
							</TableCell>
							<TableCell sx={styles.tableHead}>
								Longitude
							</TableCell>
							<TableCell sx={styles.tableHead}>
								Technology
							</TableCell>
							<TableCell sx={styles.tableHead}>
								Active Respy Calibration
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{sites.map((site) => (
							<TableRow key={site.asset_id}>
								<TableCell sx={styles.tableCell}>
									{site.specific_name}
								</TableCell>
								<TableCell sx={styles.tableCell}>
									{site.asset_id}
								</TableCell>
								<TableCell sx={styles.tableCell}>
									<ReactCountryFlag
										countryCode={site.country ?? ""}
										svg
										title={site.country ?? ""}
									/>
								</TableCell>
								<TableCell sx={styles.tableCell}>
									{site.installed_capacity_mw}
								</TableCell>
								<TableCell sx={styles.tableCell}>
									{site.latitude}
								</TableCell>
								<TableCell sx={styles.tableCell}>
									{site.longitude}
								</TableCell>
								<TableCell sx={styles.tableCell}>
									{site.techno?.name}
								</TableCell>
								<TableCell sx={styles.tableCell}>
									<ActiveCalibrationField
										value={site.active_respy_calibration}
										site={site}
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
}
